import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

const tabId = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
export const pillCls = "inline-flex items-center justify-center rounded-full border border-neutral-900 px-10 py-2.5 text-[10px] tracking-[0.2em] uppercase hover:bg-neutral-900 hover:text-white transition-colors";

function OverlayHero({ b }: any) {
  return (
    <section className="relative overflow-hidden min-h-[440px] md:min-h-0 md:h-[360px] lg:h-[400px]" data-testid="edu-hero">
      {b.image && <img src={b.image} alt="" className={`absolute inset-0 w-full h-full object-cover ${b.grayscale ? "grayscale" : ""}`} />}
      <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-white/90 via-white/60 to-transparent" />
      <div className="relative z-10 h-full min-h-[440px] md:min-h-0 max-w-[1400px] mx-auto px-6 md:px-12 flex items-end md:items-center pb-10 md:pb-0">
        <div className="max-w-md text-center mx-auto md:mx-0 md:ml-[3%]">
          <h1 className="font-display uppercase tracking-[0.08em] text-3xl sm:text-4xl leading-tight rise" data-testid="edu-hero-title">{b.heading}</h1>
          {b.body && <p className="mt-5 text-sm leading-relaxed rise rise-2">{b.body}</p>}
          {b.cta_label && b.cta_url && <Link to={b.cta_url} className={`${pillCls} mt-7 rise rise-3`} data-testid="edu-hero-cta">{b.cta_label}</Link>}
        </div>
      </div>
    </section>
  );
}

export function SplitHero({ b }: any) {
  if (b.overlay) return <OverlayHero b={b} />;
  return (
    <section className={`grid md:grid-cols-2 overflow-hidden ${b.bg ? "" : "bg-[#F4F3F1]"}`} style={b.bg ? { backgroundColor: b.bg } : undefined} data-testid="edu-hero">
      <div className="order-2 md:order-1 flex flex-col items-center justify-center text-center px-6 md:px-12 py-12 md:py-16">
        <h1 className="font-display uppercase tracking-[0.08em] text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-xl rise" data-testid="edu-hero-title">{b.heading}</h1>
        {b.body && <div className="mt-6 max-w-md text-sm md:text-[15px] leading-relaxed space-y-4 rise rise-2">{b.body.split(/\n\n+/).map((t: string, i: number) => <p key={i}>{t}</p>)}</div>}
        {b.cta_label && b.cta_url && <Link to={b.cta_url} className={`${pillCls} mt-8 rise rise-3`} data-testid="edu-hero-cta">{b.cta_label}</Link>}
      </div>
      <div className="order-1 md:order-2 h-[40vh] md:h-[460px] lg:h-[520px]">{b.image && <img src={b.image} alt="" className="w-full h-full object-cover" />}</div>
    </section>
  );
}

export function PageTabs({ b }: any) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const [back, setBack] = useState(false);
  const items = (b.items || []).filter((x: any) => x.label && x.url);
  const isOn = (x: any) => x.url === pathname || (x.also || []).includes(pathname);
  const current = items.find(isOn)?.url || "";
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const a = el.querySelector<HTMLElement>('[data-active="true"]');
    if (a) el.scrollLeft = a.offsetLeft - el.clientWidth / 2 + a.clientWidth / 2;
    const check = () => { setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 4); setBack(el.scrollLeft > 4); };
    check();
    el.addEventListener("scroll", check);
    window.addEventListener("resize", check);
    return () => { el.removeEventListener("scroll", check); window.removeEventListener("resize", check); };
  }, [pathname, items.length]);
  if (!items.length) return null;
  return (
    <>
      <div className="md:hidden px-5 py-4 border-b border-neutral-200" data-testid="page-tabs-mobile">
        <select value={current} onChange={(e) => e.target.value && nav(e.target.value)} className="w-full border border-neutral-900 bg-transparent px-4 py-3 eyebrow outline-none" data-testid="page-tabs-select">
          <option value="" disabled>Select</option>
          {items.map((x: any) => <option key={x.url} value={x.url}>{x.label}</option>)}
        </select>
      </div>
      <nav className="hidden md:block border-b border-neutral-200" data-testid="page-tabs">
        <div className="max-w-[1400px] mx-auto px-6 flex items-center">
          {back && <button onClick={() => ref.current?.scrollBy({ left: -320, behavior: "smooth" })} className="pr-4 py-3 hover:-translate-x-0.5 transition-transform" aria-label="Previous guides" data-testid="page-tabs-prev"><ArrowLeft size={18} strokeWidth={1.2} /></button>}
          <div ref={ref} className={`flex-1 flex gap-8 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${back && more ? "[mask-image:linear-gradient(to_right,transparent,#000_28px,#000_calc(100%-28px),transparent)]" : more ? "[mask-image:linear-gradient(to_right,#000_calc(100%-28px),transparent)]" : back ? "[mask-image:linear-gradient(to_right,transparent,#000_28px)]" : ""}`}>
            {items.map((x: any) => { const on = isOn(x); return (
              <Link key={x.url} to={x.url} data-active={on} data-testid={`page-tab-${tabId(x.label)}`}
                className={`py-4 text-[11px] tracking-[0.14em] uppercase whitespace-nowrap border-b-2 transition-colors ${on ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-600 hover:text-neutral-900"}`}>{x.label}</Link>
            ); })}
          </div>
          {more && <button onClick={() => ref.current?.scrollBy({ left: 320, behavior: "smooth" })} className="pl-4 py-3 hover:translate-x-0.5 transition-transform" aria-label="More guides" data-testid="page-tabs-next"><ArrowRight size={18} strokeWidth={1.2} /></button>}
        </div>
      </nav>
    </>
  );
}
